import { useState } from "react";
import { 
  CheckCircle2, Clock, Circle, Loader2, Lock, ChevronDown, ChevronUp,
  User, Home, Wrench, Save, Euro, Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { COUT_UNITAIRE } from "@/utils/simulateurUtils";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
const fmtC = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

const STEP_STATUS = {
  not_started: { label: "Non démarré", icon: Circle, color: "text-muted-foreground", bg: "bg-muted" },
  in_progress: { label: "En cours", icon: Loader2, color: "text-blue-600", bg: "bg-blue-500/10" },
  done: { label: "Terminé", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10" },
};

// ═══ ÉTAPES TAB — Per logement ═══
export function SCIEtapesTab({ dossier, dossierId, fetchDossier }) {
  const [savingStep, setSavingStep] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);
  const [stepAmounts, setStepAmounts] = useState({});
  const [stepDates, setStepDates] = useState({});
  const [showTravauxPopup, setShowTravauxPopup] = useState(null); // { logId, stepKey }
  const [travauxQtes, setTravauxQtes] = useState({});
  const logements = dossier.logements || [];
  const userRole = localStorage.getItem("label_energie_role") || "";
  const isAdmin = userRole === "admin_full" || userRole === "admin_restricted" || userRole === "administratif";
  const isRenova = userRole === "admin_full";

  const TRAVAUX_ITEMS = [
    { key: "combles", label: "Isolation combles", unit: "m²", price: 20 },
    { key: "planchersBas", label: "Isolation plancher bas", unit: "m²", price: 22 },
    { key: "rampant", label: "Rampant", unit: "m²", price: 50 },
    { key: "murs", label: "Isolation murs intérieurs", unit: "m²", price: 50 },
    { key: "fenetres", label: "Fenêtres", unit: "unité", price: 450 },
    { key: "monoSplit", label: "Mono Split (1 split)", unit: "unité", price: 1300 },
    { key: "radiateurs", label: "Radiateurs électriques", unit: "unité", price: 170 },
    { key: "vmcSimple", label: "VMC Simple flux", unit: "unité", price: 600 },
    { key: "coutsFixes", label: "Coûts fixes", unit: "forfait", price: 545 },
  ];

  // Group by building address
  const grouped = {};
  logements.forEach(l => {
    const addr = l.building_adresse || "Inconnu";
    if (!grouped[addr]) grouped[addr] = [];
    grouped[addr].push(l);
  });
  // Sort within groups by occupant name
  Object.values(grouped).forEach(arr => arr.sort((a, b) => (a.nom_occupant || "").localeCompare(b.nom_occupant || "")));

  const handleStepChange = async (logId, stepKey, newStatus) => {
    // For devis_signe on 174/cumule logements: require travaux popup first
    if (newStatus === "done" && (stepKey === "devis_signe") && isAdmin) {
      const log = logements.find(l => l.id === logId);
      if (log && (log.programme === "174" || log.programme === "cumule")) {
        // Check amount is set
        const amountKey = `${logId}-${stepKey}`;
        const amount = stepAmounts[amountKey] || log.steps?.find(s => s.key === stepKey)?.amount;
        if (!amount || amount <= 0) {
          toast.error("Veuillez d'abord saisir le montant du devis");
          return;
        }
        // Open travaux popup
        const existingQtes = {};
        (log.travaux_detail || []).forEach(t => { existingQtes[t.key] = t.qty || 0; });
        setTravauxQtes(existingQtes);
        setShowTravauxPopup({ logId, stepKey, logName: log.nom_occupant, programme: log.programme });
        return;
      }
    }

    setSavingStep(`${logId}-${stepKey}`);
    try {
      const payload = { status: newStatus };
      const amountKey = `${logId}-${stepKey}`;
      if (stepAmounts[amountKey]) payload.amount = parseFloat(stepAmounts[amountKey]);
      if (stepDates[amountKey]) payload.completion_date = stepDates[amountKey];
      await fetch(`${API}/dossiers/${dossierId}/logements/${logId}/steps/${stepKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      toast.success("Étape mise à jour");
      fetchDossier();
    } catch (err) { toast.error("Erreur"); }
    setSavingStep(null);
  };

  const handleConfirmTravaux = async () => {
    if (!showTravauxPopup) return;
    const { logId, stepKey } = showTravauxPopup;
    // Build travaux_detail array
    const travaux = TRAVAUX_ITEMS.map(t => ({
      key: t.key, label: t.label, qty: travauxQtes[t.key] || 0, unit: t.unit,
      unit_price: t.price, total: (travauxQtes[t.key] || 0) * t.price,
    })).filter(t => t.qty > 0);
    const totalCost = travaux.reduce((s, t) => s + t.total, 0);

    setSavingStep(`${logId}-${stepKey}`);
    try {
      // Save travaux to logement
      await fetch(`${API}/dossiers/${dossierId}/logements/${logId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ travaux_detail: travaux }),
      });
      // Now validate the step
      const amountKey = `${logId}-${stepKey}`;
      const payload = { status: "done" };
      if (stepAmounts[amountKey]) payload.amount = parseFloat(stepAmounts[amountKey]);
      if (stepDates[amountKey]) payload.completion_date = stepDates[amountKey];
      await fetch(`${API}/dossiers/${dossierId}/logements/${logId}/steps/${stepKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      toast.success("Travaux enregistrés et étape validée");
      setShowTravauxPopup(null);
      fetchDossier();
    } catch (err) { toast.error("Erreur"); }
    setSavingStep(null);
  };

  return (
    <div className="space-y-4">
      {/* Global progress */}
      <div className="border border-border rounded-sm bg-card p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold">Progression globale</span>
          <span className="text-sm font-bold">{Math.round(dossier.steps_progress || 0)}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${dossier.steps_progress || 0}%` }} />
        </div>
      </div>

      {Object.entries(grouped).map(([addr, logs]) => (
        <div key={addr} className="border border-border rounded-sm overflow-hidden">
          <div className="p-3 bg-muted/30 border-b border-border flex items-center gap-2">
            <Building2 className="w-4 h-4 text-emerald-600" />
            <span className="font-semibold text-sm">{addr}</span>
            <span className="text-xs text-muted-foreground">({logs.length} logements)</span>
          </div>
          {logs.map((log) => {
            const isExpanded = expandedLog === log.id;
            const steps = log.steps || [];
            const doneCount = steps.filter(s => s.status === "done").length;
            const pct = steps.length > 0 ? Math.round((doneCount / steps.length) * 100) : 0;
            return (
              <div key={log.id} className="border-b border-border/50 last:border-0">
                <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-muted/20"
                  onClick={() => setExpandedLog(isExpanded ? null : log.id)} data-testid={`etape-log-${log.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-bold">{log.num}</span>
                    <div>
                      <p className="text-sm font-semibold">{log.nom_occupant} <span className="text-xs text-muted-foreground font-normal">— {log.surface}m² · {log.type_logement}</span></p>
                      <p className="text-[10px] text-muted-foreground">{log.programme === "cumule" ? "Op. Cumulée" : "174"} — {doneCount}/{steps.length} étapes</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-20 bg-muted rounded-full h-1.5">
                      <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-bold">{pct}%</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-border/50 divide-y divide-border/30">
                    {steps.map((step, si) => {
                      const cfg = STEP_STATUS[step.status] || STEP_STATUS.not_started;
                      const Icon = cfg.icon;
                      const isSaving = savingStep === `${log.id}-${step.key}`;
                      const isLocked = si > 0 && steps[si - 1]?.status !== "done";
                      const amountKey = `${log.id}-${step.key}`;
                      const needsAmount = isAdmin && ["devis_signe", "paye", "prime_cee", "prime_apa", "prime_mpr", "mpr_devis_signe"].includes(step.key);
                      const needsDate = isAdmin && ["devis_signe", "date_demarrage_travaux", "travaux_realises", "cofrac", "mpr_devis_signe", "dossier_a_installer", "dossier_installe"].includes(step.key);
                      return (
                        <div key={step.key} className={cn("px-4 py-2.5", isLocked && "opacity-50")}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={cn("w-7 h-7 rounded-full flex items-center justify-center", cfg.bg)}>
                                {isLocked ? <Lock className="w-3.5 h-3.5 text-muted-foreground" /> : <Icon className={cn("w-3.5 h-3.5", cfg.color)} />}
                              </div>
                              <div>
                                <p className="text-xs font-medium">{step.name}</p>
                                {step.updated_at && step.status === "done" && (
                                  <p className="text-[10px] text-emerald-600">Validé le {new Date(step.updated_at).toLocaleDateString("fr-FR")}</p>
                                )}
                                {step.amount > 0 && <p className="text-[10px] text-muted-foreground">Montant : {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(step.amount)}</p>}
                              </div>
                            </div>
                            <Select value={step.status} onValueChange={(v) => handleStepChange(log.id, step.key, v)} disabled={isSaving || isLocked}>
                              <SelectTrigger className={cn("w-[130px] h-7 rounded-sm text-xs", cfg.bg)}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="not_started"><div className="flex items-center gap-1.5"><Circle className="w-3 h-3 text-muted-foreground" />Non démarré</div></SelectItem>
                                <SelectItem value="in_progress"><div className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 text-blue-600" />En cours</div></SelectItem>
                                <SelectItem value="done"><div className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-600" />Terminé</div></SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {/* Amount and Date inputs for admin */}
                          {(needsAmount || needsDate) && !isLocked && (
                            <div className="flex items-center gap-2 mt-1.5 ml-10">
                              {needsAmount && (
                                <Input type="number" placeholder="Montant €" className="h-7 w-28 text-xs rounded-sm"
                                  value={stepAmounts[amountKey] || step.amount || ""}
                                  onChange={e => setStepAmounts(prev => ({ ...prev, [amountKey]: e.target.value }))} />
                              )}
                              {needsDate && (
                                <Input type="date" className="h-7 w-36 text-xs rounded-sm"
                                  value={stepDates[amountKey] || step.completion_date || ""}
                                  onChange={e => setStepDates(prev => ({ ...prev, [amountKey]: e.target.value }))} />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Travaux Popup — 174/cumule devis validation */}
      <Dialog open={!!showTravauxPopup} onOpenChange={() => setShowTravauxPopup(null)}>
        <DialogContent className="rounded-xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Détail des travaux — Devis 174</DialogTitle>
            <p className="text-xs text-muted-foreground">{showTravauxPopup?.logName}</p>
          </DialogHeader>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="p-3 bg-muted/30 border-b border-border">
              <span className="text-xs font-bold">Travaux par logement</span>
            </div>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-border bg-muted/20">
                <th className="text-left px-3 py-2 text-emerald-600 font-semibold">Travail</th>
                <th className="text-center px-3 py-2 text-emerald-600 font-semibold">Quantité</th>
                <th className="text-center px-3 py-2">Unité</th>
                {isRenova && <th className="text-right px-3 py-2">Coût unit.</th>}
                {isRenova && <th className="text-right px-3 py-2">Total</th>}
              </tr></thead>
              <tbody>
                {TRAVAUX_ITEMS.map(t => (
                  <tr key={t.key} className="border-b border-border/30">
                    <td className="px-3 py-2 font-medium">{t.label}</td>
                    <td className="px-3 py-2 text-center">
                      <Input type="number" min="0" className="h-7 w-16 text-xs text-center rounded-sm mx-auto" value={travauxQtes[t.key] || 0}
                        onChange={e => setTravauxQtes(prev => ({ ...prev, [t.key]: parseInt(e.target.value) || 0 }))} />
                    </td>
                    <td className="px-3 py-2 text-center text-muted-foreground">{t.unit}</td>
                    {isRenova && <td className="px-3 py-2 text-right">{t.price} €</td>}
                    {isRenova && <td className="px-3 py-2 text-right font-semibold">{(travauxQtes[t.key] || 0) > 0 ? `${(travauxQtes[t.key] || 0) * t.price} €` : "—"}</td>}
                  </tr>
                ))}
              </tbody>
              {isRenova && (
                <tfoot>
                  <tr className="border-t-2 border-emerald-500/40 bg-emerald-500/10">
                    <td colSpan="4" className="px-3 py-2 font-bold">Total travaux</td>
                    <td className="px-3 py-2 text-right font-bold">
                      {TRAVAUX_ITEMS.reduce((s, t) => s + (travauxQtes[t.key] || 0) * t.price, 0)} €
                    </td>
                  </tr>
                </tfoot>

// ═══ INFORMATIONS TAB — Per logement (editable) ═══
              )}
            </table>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowTravauxPopup(null)} className="flex-1 rounded-lg">Annuler</Button>
            <Button onClick={handleConfirmTravaux} className="flex-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
              Enregistrer et valider l'étape
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
export function SCIInfoTab({ dossier, dossierId, fetchDossier }) {
  const [editingLog, setEditingLog] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const logements = dossier.logements || [];
  const sci = dossier.sci_info || {};

  const grouped = {};
  logements.forEach(l => {
    const addr = l.building_adresse || "Inconnu";
    if (!grouped[addr]) grouped[addr] = [];
    grouped[addr].push(l);
  });
  Object.values(grouped).forEach(arr => arr.sort((a, b) => (a.nom_occupant || "").localeCompare(b.nom_occupant || "")));

  const startEdit = (log) => {
    setEditingLog(log.id);
    setEditData({ ...log });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/dossiers/${dossierId}/logements/${editingLog}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editData),
      });
      toast.success("Logement mis à jour");
      setEditingLog(null);
      fetchDossier();
    } catch (err) { toast.error("Erreur"); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      {/* SCI Info */}
      <div className="border border-border rounded-sm bg-card p-5">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><Building2 className="w-4 h-4 text-blue-500" />Informations SCI</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <InfoCell label="Nom SCI" value={sci.nom_sci || dossier.client_name} />
          <InfoCell label="Contact" value={sci.nom_contact || dossier.nom_representant} />
          <InfoCell label="Téléphone" value={sci.tel_contact || dossier.tel_representant} />
          <InfoCell label="Email" value={sci.email || dossier.client_email} />
          <InfoCell label="Adresse SCI" value={sci.adresse_sci || dossier.adresse_bailleur} />
          <InfoCell label="Code postal" value={sci.code_postal_sci || dossier.code_postal} />
        </div>
      </div>

      {/* Logements */}
      {Object.entries(grouped).map(([addr, logs]) => (
        <div key={addr} className="border border-border rounded-sm overflow-hidden">
          <div className="p-3 bg-muted/30 border-b border-border flex items-center gap-2">
            <Home className="w-4 h-4 text-emerald-600" />
            <span className="font-semibold text-sm">{addr}</span>
          </div>
          {logs.map((log) => {
            const isEditing = editingLog === log.id;
            return (
              <div key={log.id} className="border-b border-border/50 last:border-0 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold">{log.num}</span>
                    <span className="font-semibold text-sm">{log.nom_occupant}</span>
                    <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-bold", log.type_logement === "maison" ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" : "bg-blue-500/20 text-blue-600 dark:text-blue-400")}>
                      {log.type_logement === "maison" ? "Maison" : "Appt."}
                    </span>
                  </div>
                  {!isEditing ? (
                    <Button variant="outline" size="sm" onClick={() => startEdit(log)} className="rounded-sm h-7 text-xs">Modifier</Button>
                  ) : (
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => setEditingLog(null)} className="rounded-sm h-7 text-xs">Annuler</Button>
                      <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-sm h-7 text-xs">{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Sauver</Button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <EditField label="Nom occupant" value={editData.nom_occupant} onChange={v => setEditData(p => ({...p, nom_occupant: v}))} />
                    <EditField label="Téléphone" value={editData.tel_occupant} onChange={v => setEditData(p => ({...p, tel_occupant: v}))} />
                    <EditField label="Surface (m²)" value={editData.surface} onChange={v => setEditData(p => ({...p, surface: parseFloat(v) || 0}))} type="number" />
                    <div>
                      <label className="text-[10px] text-muted-foreground">Type</label>
                      <Select value={editData.type_logement} onValueChange={v => setEditData(p => ({...p, type_logement: v}))}>
                        <SelectTrigger className="h-7 text-xs rounded-sm mt-0.5"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="maison">Maison</SelectItem><SelectItem value="appartement">Appartement</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <EditField label="Chauffage" value={editData.chauffage} onChange={v => setEditData(p => ({...p, chauffage: v}))} />
                    <EditField label="DPE" value={editData.dpe} onChange={v => setEditData(p => ({...p, dpe: v}))} />
                    <EditField label="Précarité" value={editData.precarite} onChange={v => setEditData(p => ({...p, precarite: v}))} />
                    <EditField label="Age" value={editData.age} onChange={v => setEditData(p => ({...p, age: v}))} />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <InfoCell label="Téléphone" value={log.tel_occupant} />
                    <InfoCell label="Surface" value={`${log.surface} m²`} />
                    <InfoCell label="Chauffage" value={log.chauffage} />
                    <InfoCell label="DPE" value={log.dpe} />
                    <InfoCell label="Précarité" value={log.precarite} />
                    <InfoCell label="Zone" value={log.zone} />
                    <InfoCell label="Programme" value={log.programme === "cumule" ? "Op. Cumulée" : "174"} />
                    <InfoCell label="Gestes déjà" value={log.gestes_deja?.length ? log.gestes_deja.join(", ") : "Aucun"} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ═══ TRAVAUX TAB — Per logement ═══
export function SCITravauxTab({ dossier, dossierId, fetchDossier }) {
  const [expandedLog, setExpandedLog] = useState(null);
  const [editingTravaux, setEditingTravaux] = useState(null);
  const [travauxEdits, setTravauxEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const logements = dossier.logements || [];
  const userRole = localStorage.getItem("label_energie_role") || "";
  const isRenova = userRole === "admin_full";
  const isAdmin = userRole === "admin_full" || userRole === "admin_restricted" || userRole === "administratif";

  const grouped = {};
  logements.forEach(l => {
    const addr = l.building_adresse || "Inconnu";
    if (!grouped[addr]) grouped[addr] = [];
    grouped[addr].push(l);
  });
  Object.values(grouped).forEach(arr => arr.sort((a, b) => (a.nom_occupant || "").localeCompare(b.nom_occupant || "")));

  const handleSaveTravaux = async (logId) => {
    setSaving(true);
    try {
      const edits = travauxEdits[logId] || {};
      await fetch(`${API}/dossiers/${dossierId}/logements/${logId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ travaux_detail: edits }),
      });
      toast.success("Travaux mis à jour");
      setEditingTravaux(null);
      fetchDossier?.();
    } catch (err) { toast.error("Erreur"); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([addr, logs]) => (
        <div key={addr} className="border border-border rounded-sm overflow-hidden">
          <div className="p-3 bg-muted/30 border-b border-border flex items-center gap-2">
            <Building2 className="w-4 h-4 text-emerald-600" />
            <span className="font-semibold text-sm">{addr}</span>
          </div>
          {logs.map((log) => {
            const isExpanded = expandedLog === log.id;
            const travaux = log.travaux_detail || [];
            const sim = log.simulation || {};
            const isEditingThis = editingTravaux === log.id;
            return (
              <div key={log.id} className="border-b border-border/50 last:border-0">
                <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-muted/20"
                  onClick={() => setExpandedLog(isExpanded ? null : log.id)}>
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-bold">{log.num}</span>
                    <div>
                      <p className="text-sm font-semibold">{log.nom_occupant} — {log.surface} m²</p>
                      <p className="text-[10px] text-muted-foreground">{sim.scenario || "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isRenova && <span className="font-bold text-emerald-600 text-sm">Marge: {fmtC(sim.marge)}</span>}
                    <span className="font-bold text-sm">CA: {fmtC(sim.caTTC)}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>
                {isExpanded && travaux.length > 0 && (
                  <div className="border-t border-emerald-500/30 mx-3 mb-3">
                    {isAdmin && !isEditingThis && (
                      <div className="p-2 flex justify-end">
                        <button onClick={(e) => { e.stopPropagation(); setEditingTravaux(log.id); setTravauxEdits(prev => ({ ...prev, [log.id]: [...travaux] })); }} className="text-[10px] text-blue-600 font-bold hover:underline">Modifier les travaux</button>
                      </div>
                    )}
                    <div className="divide-y divide-border/30">
                      {(isEditingThis ? (travauxEdits[log.id] || travaux) : travaux).map((t, ti) => (
                        <div key={ti} className="flex items-center justify-between px-4 py-2 text-xs gap-2">
                          <span className="font-medium flex-1">{t.label}</span>
                          {isEditingThis ? (
                            <Input type="number" className="w-16 h-6 text-xs text-right rounded-sm" value={(travauxEdits[log.id] || [])[ti]?.qty || t.qty}
                              onChange={e => {
                                const newQty = parseFloat(e.target.value) || 0;
                                setTravauxEdits(prev => {
                                  const arr = [...(prev[log.id] || travaux)];
                                  arr[ti] = { ...arr[ti], qty: newQty, total: Math.round(newQty * arr[ti].unit_price * 100) / 100 };
                                  return { ...prev, [log.id]: arr };
                                });
                              }} />
                          ) : (
                            <span className="text-muted-foreground">{t.qty} {t.unit}</span>
                          )}
                          {isRenova && <span className="text-muted-foreground">x {fmtC(t.unit_price)}</span>}
                          {isRenova && <span className="font-semibold">{fmtC(t.total)}</span>}
                        </div>
                      ))}
                    </div>
                    {isEditingThis && (
                      <div className="p-2 flex justify-end gap-2">
                        <button onClick={() => setEditingTravaux(null)} className="text-xs text-muted-foreground">Annuler</button>
                        <button onClick={() => handleSaveTravaux(log.id)} disabled={saving} className="text-xs text-emerald-600 font-bold">{saving ? "..." : "Enregistrer"}</button>
                      </div>
                    )}
                    <div className="border-t-2 border-emerald-500/40 px-4 py-2 flex items-center justify-between bg-emerald-500/10">
                      <span className="text-xs font-bold">CA TTC</span>
                      <span className="font-bold">{fmtC(sim.caTTC)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <div>
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <p className="text-xs font-semibold">{value || "—"}</p>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] text-muted-foreground">{label}</label>
      <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)} className="h-7 text-xs rounded-sm mt-0.5" />
    </div>
  );
}
